import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { toast } from 'react-toastify';
import { addImageElement, addRichTextElement } from '../../redux/TabTile/TabActions';
import { ImageElementType, RichTextElement } from '../../redux/TabTile/TabStateInterfaces';

interface ClipboardPayload {
  dataType: 'image-element' | 'rich-text';
  data: ImageElementType | RichTextElement;
}

const useDashboardClipboard = () => {
  const dispatch = useDispatch();

  const {
    activeTabId,
    selectedImageId,
    imageElements,
    richTextElementId,
    richTextElements,
    dashMode,
  } = useSelector((state: RootState) => {
    const activeTabId = state.tabTileProps.selectedTabId;
    const tabData = state.tabState.tabs[activeTabId]?.dashboardObjects ?? {};

    return {
      activeTabId,
      selectedImageId: tabData.selectedImageId ?? null,
      imageElements: tabData.imageElements ?? [],
      richTextElementId: tabData.richTextElementId ?? null,
      richTextElements: tabData.richTextElements ?? [],
      dashMode: state.tabTileProps.dashMode,
    };
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (dashMode === 'Present') return;
      // Ignore input fields so we don't paste while typing inside a text box
      const target = e.target as HTMLElement;
      const isFormInput = ['INPUT', 'TEXTAREA'].includes(target.tagName);
      const isSlateEditor = target.isContentEditable;

      const isModifierPressed = e.ctrlKey || e.metaKey;
      const key = e.key.toLowerCase();

      // Check if user has specifically highlighted text
      const selection = window.getSelection();
      const hasTextSelection = selection && selection.toString().length > 0;

      // If we are inside an input or editor, ALWAYS return to allow native text paste.
      // We never want to paste a Dashboard Object while typing.
      if (isModifierPressed && key === 'v' && (isFormInput || isSlateEditor)) {
        return;
      }
      // If we are inside an input/editor AND have text selected, return to allow native text copy.
      // If we are inside an editor but NO text is selected, proceed (we wants to copy the Component).
      if (isModifierPressed && key === 'c' && (isFormInput || isSlateEditor) && hasTextSelection) {
        return;
      }

      // COPY LOGIC (Ctrl + C)

      if (isModifierPressed && key === 'c') {
        let payload: ClipboardPayload | null = null;
        let objectLabel = '';

        //   an Image or Button is selected
        if (selectedImageId) {
          const itemToCopy = imageElements.find((el: any) => el.id === selectedImageId);
          if (itemToCopy) {
            payload = { dataType: 'image-element', data: itemToCopy };
            objectLabel = itemToCopy.type === 'icon-button' ? 'Button' : 'Image';
          }
        }
        //  if Rich Text is selected
        else if (richTextElementId) {
          const itemToCopy = richTextElements.find((el: any) => el.id === richTextElementId);
          if (itemToCopy) {
            payload = { dataType: 'rich-text', data: itemToCopy };
            objectLabel = 'Rich Text';
          }
        }

        // Save to LocalStorage
        if (payload) {
          localStorage.setItem('dashboard_clipboard', JSON.stringify(payload));

          toast.success(`Copied ${objectLabel}!`, {
            position: 'top-right',
            autoClose: 2000,
          });
        }
      }

      // ===========================
      // PASTE LOGIC (Ctrl + V)
      // ===========================
      if (isModifierPressed && key === 'v') {
        const clipboardRaw = localStorage.getItem('dashboard_clipboard');

        if (clipboardRaw) {
          try {
            const parsedPayload = JSON.parse(clipboardRaw) as ClipboardPayload;

            // Handle IMAGE / BUTTON Paste
            if (parsedPayload.dataType === 'image-element') {
              const itemData = parsedPayload.data as ImageElementType;

              // 1Button or an Image
              const isButton = itemData.type === 'icon-button';
              const objectLabel = isButton ? 'Button' : 'Image';
              const prefix = isButton ? 'btn' : 'img';

              const newId = `${prefix}-${Date.now()}`;

              const newElement: ImageElementType = {
                ...itemData,
                id: newId,
                x: (itemData.x || 0) + 2,
                y: (itemData.y || 0) + 2,
              };

              dispatch(addImageElement(activeTabId, newElement));

              // the specific label in the toast
              toast.success(`Pasted ${objectLabel}!`, {
                position: 'top-right',
                autoClose: 2000,
              });
            }

            // Handle RICH TEXT Paste
            else if (parsedPayload.dataType === 'rich-text') {
              const itemData = parsedPayload.data as RichTextElement;
              const newId = String(Date.now());

              const newElement: RichTextElement = {
                ...itemData,
                id: newId,
                x: (itemData.x || 0) + 2,
                y: (itemData.y || 0) + 2,
              };

              dispatch(addRichTextElement(activeTabId, newElement));

              toast.success('Pasted Rich Text!', {
                position: 'top-right',
                autoClose: 2000,
              });
            }

            // Remove from localstorage after pasting
            localStorage.removeItem('dashboard_clipboard');
          } catch (error) {
            console.error('Failed to paste object:', error);
            toast.error('Failed to paste object.', { autoClose: 2000 });
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    activeTabId,
    imageElements,
    richTextElements,
    selectedImageId,
    richTextElementId,
    dispatch,
    dashMode,
  ]);
};

export default useDashboardClipboard;
